if (!customElements.get('bracelet-builder')) {
  customElements.define(
    'bracelet-builder',
    class BraceletBuilder extends HTMLElement {
      connectedCallback() {
        this.strings = this.readJSON('[data-strings]', {});
        this.priceVariants = this.readJSON('[data-price-variants]', {});

        this.priceIndex = new Map();
        for (const [price, id] of Object.entries(this.priceVariants)) {
          const value = Number(price);
          if (Number.isFinite(value)) this.priceIndex.set(value, id);
        }
        this.locale = this.dataset.locale || 'ka';
        this.priceStep = parseFloat(this.dataset.priceStep) || 5;

        this.assetBase = (this.dataset.configUrl || '').replace(/[^/]*$/, '');
        this.ghostUrl = this.dataset.ghostUrl || '';

        this.el = {
          shell: this.querySelector('[data-bb-shell]'),
          nav: this.querySelector('[data-bb-nav]'),
          panels: this.querySelector('[data-bb-panels]'),
          slots: this.querySelector('[data-bb-slots]'),
          cord: this.querySelector('[data-bb-cord]'),
          clasp: this.querySelector('[data-bb-clasp]'),
          preview: this.querySelector('[data-bb-preview]'),
          previewInner: this.querySelector('[data-bb-preview-inner]'),
          front: this.querySelector('[data-bb-front]'),
          base: this.querySelector('[data-bb-base]'),
          lock: this.querySelector('[data-bb-lock]'),
          spec: this.querySelector('[data-bb-spec]'),
          ledger: this.querySelector('[data-bb-ledger]'),
          price: this.querySelector('[data-bb-price]'),
          progress: this.querySelector('[data-bb-progress]'),
          add: this.querySelector('[data-bb-add]'),
          qty: this.querySelector('[data-bb-qty]'),
          clear: this.querySelector('[data-bb-clear]'),
          error: this.querySelector('[data-bb-error]'),
          loader: this.querySelector('[data-bb-loader]'),
          styleName: this.querySelector('[data-bb-style-name]'),
          metaToggle: this.querySelector('[data-bb-meta-toggle]'),
          metaPanel: this.querySelector('[data-bb-meta-panel]'),
        };

        this.state = {
          styleId: null,
          sizeId: null,
          beadSize: null,
          cordColorId: null,
          claspId: null,
          slots: [],
          openPanel: null,

          armedBeadId: null,
          audience: 'male',
        };

        this.bindStaticHandlers();
        this.load();
      }

      disconnectedCallback() {
        if (this.onResize) window.removeEventListener('resize', this.onResize);
        if (this.onKeydown) document.removeEventListener('keydown', this.onKeydown);
      }

      readJSON(selector, fallback) {
        const node = this.querySelector(selector);
        if (!node) return fallback;
        try {
          return JSON.parse(node.textContent);
        } catch (error) {
          console.error('bracelet-builder: bad JSON in', selector, error);
          return fallback;
        }
      }

      async load() {
        this.setLoading(true);
        try {
          const response = await fetch(this.dataset.configUrl, {
            headers: { Accept: 'application/json' },
          });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          this.config = await response.json();
        } catch (error) {
          console.error('bracelet-builder: could not load config', error);
          this.showError(this.strings.error);
          this.setLoading(false);
          return;
        }

        this.applyMetaobjects();

        const settings = this.config.settings || {};
        if (settings.priceStep) this.priceStep = settings.priceStep;

        this.slotGeometryTable = null;
        const geometryUrl = this.dataset.geometryUrl || (this.config.artwork || {}).geometryUrl;
        if (geometryUrl) {
          try {
            const response = await fetch(geometryUrl, { headers: { Accept: 'application/json' } });
            if (response.ok) this.slotGeometryTable = await response.json();
          } catch (error) {
            console.warn('bracelet-builder: slot geometry unavailable, using computed layout', error);
          }
        }

        const style =
          this.findStyle(settings.defaultStyle) || (this.config.styles || [])[0];
        if (!style) {
          this.showError(this.strings.error);
          this.setLoading(false);
          return;
        }

        this.selectStyle(style.id, settings.defaultSize, { silent: true });
        this.renderNav();
        this.renderPanels();
        this.setLoading(false);
        this.refresh();
      }

      bindStaticHandlers() {
        this.el.add.addEventListener('click', () => this.addToCart());
        this.el.clear.addEventListener('click', () => this.clearSlots());

        this.el.metaToggle.addEventListener('click', () => {
          if (!this.classList.contains('bb--mobile')) return;
          const open = this.el.metaToggle.getAttribute('aria-expanded') === 'true';
          this.el.metaToggle.setAttribute('aria-expanded', String(!open));
          this.classList.toggle('bb--meta-open', !open);
        });

        this.onResize = this.debounce(() => this.syncViewport(), 150);
        window.addEventListener('resize', this.onResize);
        this.syncViewport();

        this.onKeydown = (event) => {
          if (event.key !== 'Escape') return;
          if (this.state.armedBeadId) return this.disarmBead();
          if (this.state.openPanel) this.closePanel();
        };
        document.addEventListener('keydown', this.onKeydown);
      }

      syncViewport() {
        this.classList.toggle('bb--mobile', window.innerWidth <= 990);
      }

      setLoading(isLoading) {
        this.el.loader.hidden = !isLoading;
        this.classList.toggle('bb--loading', isLoading);
      }

      showError(message) {
        this.el.error.textContent = message || '';
        this.el.error.hidden = !message;
      }

      applyMetaobjects() {
        const node = this.querySelector('[data-bb-metaobjects]');
        if (!node) return;

        let meta;
        try {
          meta = JSON.parse(node.textContent);
        } catch (error) {
          console.error('bracelet-builder: could not parse metaobjects', error);
          return;
        }

        if (!meta) return;

        const SIZE_ID = { s: 'small', m: 'medium', l: 'large', xl: 'xlarge' };

        this.collections = Array.isArray(meta.collections) ? meta.collections : [];

        if (Array.isArray(meta.beads) && meta.beads.length) {
          const kinds = [
            { id: 'stone', kind: 'stone', name: { ka: 'ქვები', en: 'Stones', ru: 'Камни' } },
            { id: 'detail', kind: 'detail', name: { ka: 'დეტალები', en: 'Details', ru: 'Детали' } },
          ];

          this.config.beadGroups = kinds
            .map((group) => ({
              ...group,
              beads: meta.beads
                .filter((bead) => (bead.kind || 'stone') === group.kind)
                .map((bead) => ({
                  id: bead.id,
                  name: bead.name,
                  keywords: bead.keywords,
                  kind: bead.kind || 'stone',
                  audience: bead.audience || 'both',
                  hex: bead.hex || '',
                  price: bead.price || {},
                  image: bead.image || '',
                })),
            }))
            .filter((group) => group.beads.length > 0);
        }

        if (Array.isArray(meta.clasps) && meta.clasps.length) {
          this.config.clasps = meta.clasps.map((clasp) => {
            const existing = (this.config.clasps || []).find((c) => c.id === clasp.id) || {};
            return {
              ...existing,
              id: clasp.id,
              name: existing.name || clasp.name,
              price: Number(clasp.price) || 0,
              hex: clasp.hex || existing.hex || '',
              art: existing.art || clasp.id,
              overlay: clasp.overlay || existing.overlay || '',
            };
          });
        }

        for (const style of meta.styles || []) {
          const target = (this.config.styles || []).find((s) => s.id === style.id);
          if (!target) continue;

          if (style.name && !target.name) target.name = style.name;
          if (Number.isFinite(Number(style.basePrice))) target.basePrice = Number(style.basePrice);
          if (typeof style.hasClasp === 'boolean') target.hasClasp = style.hasClasp;
          if (Array.isArray(style.clasps) && style.clasps.length) target.claspIds = style.clasps;

          for (const size of style.sizes || []) {
            const sizeId = SIZE_ID[size.id] || size.id;
            const targetSize = (target.sizes || []).find((z) => z.id === sizeId);
            if (!targetSize) continue;

            if (size.label && !targetSize.label) targetSize.label = size.label;
            if (Number(size.lengthCm)) targetSize.lengthCm = Number(size.lengthCm);

            const counts = size.counts || {};
            if (counts.default != null) {
              targetSize.slots = { count: Number(counts.default) };
            } else if (Object.keys(counts).length) {
              targetSize.slotsByBeadSize = counts;
              targetSize.slots = { count: Number(counts['8'] ?? Object.values(counts)[0]) };
            }
          }
        }

        this.config.source = 'metaobjects';
      }

      t(value) {
        if (value == null) return '';
        if (typeof value === 'string') return value;
        return value[this.locale] || value.en || value.ka || Object.values(value)[0] || '';
      }

      findStyle(id) {
        return (this.config.styles || []).find((style) => style.id === id) || null;
      }

      currentStyle() {
        return this.findStyle(this.state.styleId);
      }

      currentSize() {
        const style = this.currentStyle();
        if (!style) return null;
        return (style.sizes || []).find((size) => size.id === this.state.sizeId) || null;
      }

      allBeads() {
        return (this.config.beadGroups || []).flatMap((group) => group.beads || []);
      }

      findBead(id) {
        return this.allBeads().find((bead) => bead.id === id) || null;
      }

      beadPrice(bead) {
        if (!bead) return 0;
        const price = bead.price;
        if (typeof price === 'number') return price;
        const size = this.state.beadSize || '8';
        return Number(price[size] != null ? price[size] : Object.values(price)[0]) || 0;
      }

      findClasp(id) {
        return (this.config.clasps || []).find((clasp) => clasp.id === id) || null;
      }

      // Clasps are one global pool; a style may name the subset it offers, in its
      // own order. Styles without a list keep the whole pool.
      // Where the clasp sits inside its full-canvas overlay, so the picker can
      // show it large instead of a speck in an otherwise empty frame.
      claspCrop() {
        const style = this.currentStyle();
        if (!style) return null;
        const rules = ((this.config.artwork || {}).styles || {})[style.id];
        const crop = rules && rules.lockCrop;
        if (!crop || !crop.size || !crop.w) return null;
        return crop;
      }

      // Theme-asset fallback for one specific clasp, not the selected one.
      lockAssetFor(clasp) {
        const style = this.currentStyle();
        const rules = ((this.config.artwork || {}).styles || {})[style && style.id];
        const template = rules && rules.lock;
        if (!template || !clasp) return '';
        const finish = clasp.art || clasp.id || '';
        const path = template
          .split('{finish}').join(String(finish).toLowerCase())
          .split('{Finish}').join(String(finish));
        if (path.includes('{')) return '';
        return ((this.config.artwork || {}).basePath || '') + path;
      }

      claspsFor(style) {
        const all = this.config.clasps || [];
        const target = style || this.currentStyle();
        const ids = target && Array.isArray(target.claspIds) ? target.claspIds : null;
        const collection = (this.collections || []).find((c) => c.id === this.state.audience);
        const allowed =
          collection && Array.isArray(collection.clasps) && collection.clasps.length
            ? new Set(collection.clasps)
            : null;
        const pool = allowed ? all.filter((c) => allowed.has(c.id)) : all;
        if (!ids || !ids.length) return pool;
        const rank = new Map(ids.map((id, i) => [id, i]));
        return pool.filter((c) => rank.has(c.id)).sort((a, b) => rank.get(a.id) - rank.get(b.id));
      }

      findCord(id) {
        return (this.config.cordColors || []).find((cord) => cord.id === id) || null;
      }

      variantKey() {
        const style = this.currentStyle();
        const size = this.currentSize();
        if (!style || !size) return null;
        const suffix = style.hasBeadSize && this.state.beadSize ? this.state.beadSize : '';
        // Stone counts differ by gender on some styles, so the audience is part of the key.
        const scoped = (style.sizes || []).some((z) => z.slotsByAudience);
        const who = scoped && this.state.audience ? `${this.state.audience}_` : '';
        return `${style.id}_${who}${size.id}${suffix}`;
      }

      // Sizes this style offers to the current audience (women get XS, men don't).
      sizesFor(style, audience) {
        const target = style || this.currentStyle();
        const who = audience || this.state.audience;
        const sizes = (target && target.sizes) || [];
        if (!who) return sizes;
        return sizes.filter((z) => !Array.isArray(z.audiences) || z.audiences.includes(who));
      }

      // Bead sizes this style offers (თოკზე is 8mm and 6mm only).
      beadSizesFor(style) {
        const target = style || this.currentStyle();
        const all = this.config.beadSizes || [];
        const ids = target && Array.isArray(target.beadSizeIds) ? target.beadSizeIds : null;
        if (!ids || !ids.length) return all;
        const rank = new Map(ids.map((id, i) => [id, i]));
        return all.filter((b) => rank.has(b.id)).sort((a, b) => rank.get(a.id) - rank.get(b.id));
      }

      geometry() {
        const key = this.variantKey();
        if (!key || !this.slotGeometryTable) return null;
        return this.slotGeometryTable.variants[key] || null;
      }

      slotCount() {
        const geometry = this.geometry();
        if (geometry && geometry.slotCount) return geometry.slotCount;

        const size = this.currentSize();
        if (!size) return 0;
        const style = this.currentStyle();
        const mm = this.state.beadSize;

        const byAudience = size.slotsByAudience && size.slotsByAudience[this.state.audience];
        if (byAudience && byAudience[mm]) return byAudience[mm];

        if (style && style.hasBeadSize && size.slotsByBeadSize) {
          const byBeadSize = size.slotsByBeadSize[mm];
          if (byBeadSize) return byBeadSize;
        }
        return size.slots.count;
      }

      resolveLayer(kind) {
        const artwork = this.config.artwork;
        const style = this.currentStyle();
        const size = this.currentSize();
        if (!artwork || !style || !size) return null;

        const rules = (artwork.styles || {})[style.id];
        const template = rules && rules[kind];

        const cord = this.findCord(this.state.cordColorId);
        const clasp = this.findClasp(this.state.claspId);

        if (kind === 'lock' && style.hasClasp) {
          if (!clasp) return null;                 // nothing chosen yet
          if (clasp.overlay) return clasp.overlay;
        }

        if (!template) return null;

        const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');

        const sizeTitle =
          cap(size.id) === 'Xlarge' ? 'XLarge' : cap(size.id) === 'Xsmall' ? 'XSmall' : cap(size.id);
        const colour = (cord && cord.art) || (cord && cap(cord.id)) || '';
        const finish = (clasp && clasp.art) || '';

        const tokens = {
          '{style}': style.id,
          '{Style}': cap(style.id),
          '{size}': size.id,
          '{Size}': sizeTitle,
          '{SizeCode}': (rules.sizeCodes || {})[size.id] || '',
          '{Colour}': colour,
          '{colour}': colour.toLowerCase(),
          '{Finish}': finish,
          '{finish}': finish.toLowerCase(),
          '{mm}': this.state.beadSize || '',
        };

        let path = template;
        for (const [token, value] of Object.entries(tokens)) path = path.split(token).join(value);
        if (path.includes('{')) return null;

        return (artwork.basePath || '') + path;
      }

      usingArtwork() {
        const hasLayer = Boolean(this.resolveLayer('front') || this.resolveLayer('base'));
        return hasLayer && !this.artworkFailed;
      }

      selectStyle(styleId, preferredSizeId, options = {}) {
        const style = this.findStyle(styleId);
        if (!style) return;

        this.state.styleId = style.id;

        const sizes = this.sizesFor(style);
        const size =
          sizes.find((candidate) => candidate.id === preferredSizeId) ||
          sizes.find((candidate) => candidate.id === this.state.sizeId) ||
          sizes[Math.floor(sizes.length / 2)] ||
          sizes[0];
        this.state.sizeId = size ? size.id : null;

        const keepOrDefault = (list, current, fallbackIndex) => {
          const items = list || [];
          if (current && items.some((item) => item.id === current)) return current;
          return (items[fallbackIndex] || items[0] || {}).id || null;
        };

        const beadSizes = this.beadSizesFor(style);
        const keepBead =
          this.state.beadSize && beadSizes.some((b) => b.id === this.state.beadSize)
            ? this.state.beadSize
            : null;
        this.state.beadSize = style.hasBeadSize
          ? keepBead ||
            (beadSizes.some((b) => b.id === '8') ? '8' : (beadSizes[0] || {}).id) ||
            '8'
          : null;

        this.state.cordColorId = style.hasCordColor
          ? keepOrDefault(this.config.cordColors, this.state.cordColorId, 0)
          : null;

        const offered = style.hasClasp ? this.claspsFor(style) : [];
        this.state.claspId =
          this.state.claspId && offered.some((c) => c.id === this.state.claspId)
            ? this.state.claspId
            : null;

        this.resetSlots();
        if (!options.silent) {
          this.renderNav();
          this.renderPanels();
          this.refresh();
        }
      }

      selectSize(sizeId) {
        this.state.sizeId = sizeId;
        this.resetSlots();
        this.refresh();
      }

      selectBeadSize(beadSize) {
        this.state.beadSize = beadSize;

        this.resetSlots();
        this.renderPanels();
        this.refresh();
      }

      selectCord(cordId) {
        this.state.cordColorId = cordId;
        this.refresh();
      }

      selectClasp(claspId) {
        this.state.claspId = claspId;
        this.refresh();
      }

      resetSlots() {
        this.state.slots = new Array(this.slotCount()).fill(null);
      }

      clearSlots() {
        this.state.slots = this.state.slots.map(() => null);
        this.refresh();
      }

      placeBead(slotIndex, beadId) {
        if (slotIndex < 0 || slotIndex >= this.state.slots.length) return;
        this.state.slots[slotIndex] = beadId;

        this.landingSlot = slotIndex;
        this.refresh();
        this.landingSlot = null;
      }

      removeBead(slotIndex) {
        if (slotIndex < 0 || slotIndex >= this.state.slots.length) return;
        this.state.slots[slotIndex] = null;
        this.refresh();
      }

      slotGeometry(index, total) {
        const size = this.currentSize();
        if (!size) return { x: 50, y: 50, angle: 0 };

        const { cx, cy, rx, ry, start, sweep } = size.slots;
        const closed = Math.abs(sweep) >= 359.5;
        const divisor = closed ? total : Math.max(1, total - 1);
        const degrees = start + (sweep * index) / divisor;

        return this.pointOnEllipse(degrees, cx, cy, rx, ry);
      }

      pointOnEllipse(degrees, cx, cy, rx, ry) {
        const radians = (degrees * Math.PI) / 180;
        return {
          x: cx + rx * Math.cos(radians),
          y: cy + ry * Math.sin(radians),
          angle: degrees + 90,
        };
      }

      claspGeometry() {
        const size = this.currentSize();
        if (!size) return null;

        const { cx, cy, rx, ry, start, sweep } = size.slots;
        if (Math.abs(sweep) >= 359.5) return null;

        const gap = 360 - Math.abs(sweep);
        const midpoint = start + sweep + gap / 2;
        return this.pointOnEllipse(midpoint, cx, cy, rx, ry);
      }

      refresh() {
        this.renderPreview();
        this.renderSpec();
        this.renderLedger();
        this.renderPrice();
        this.renderProgress();
        this.syncNavVisibility();
      }

      renderPreview() {
        const frontPath = this.resolveLayer('front');
        if (frontPath !== this.lastFrontPath) {
          this.artworkFailed = false;
          this.lastFrontPath = frontPath;
        }

        const artwork = this.usingArtwork();
        this.el.preview.classList.toggle('bb-preview--drawn', !artwork);

        if (artwork) {
          this.renderLayers();

          if (this.el.cord) this.el.cord.innerHTML = '';
          this.renderSlots();
        } else {
          this.clearLayers();

          this.renderSlots();
          this.renderCord();
        }
      }

      renderLayers() {
        const layers = [
          ['front', this.el.front],
          ['base', this.el.base],
          ['lock', this.el.lock],
        ];

        const primaryKind = this.resolveLayer('front') ? 'front' : 'base';

        for (const [kind, node] of layers) {
          if (!node) continue;
          const src = this.assetUrl(this.resolveLayer(kind));
          if (src) {
            if (node.getAttribute('src') !== src) {
              node.setAttribute('src', src);

              node.addEventListener(
                'load',
                () => {
                  if (!node.naturalWidth) return;
                  node.setAttribute('width', node.naturalWidth);
                  node.setAttribute('height', node.naturalHeight);
                },
                { once: true }
              );

              if (kind === primaryKind) {
                node.addEventListener(
                  'error',
                  () => {
                    console.warn(`bracelet-builder: artwork missing (${src}), drawing instead`);
                    this.artworkFailed = true;
                    this.renderPreview();
                  },
                  { once: true }
                );
              }
            }
            node.hidden = false;
          } else {
            node.removeAttribute('src');
            node.hidden = true;
          }
        }
      }

      clearLayers() {
        for (const node of [this.el.front, this.el.base, this.el.lock]) {
          if (!node) continue;
          node.removeAttribute('src');
          node.hidden = true;
        }
      }

      renderSlots() {
        const total = this.state.slots.length;
        const geometry = this.geometry();
        const table = geometry && geometry.slots ? geometry.slots : null;

        const slotSize = (geometry && geometry.slotSizeVw) || 5.4;
        const beadSize = (geometry && geometry.beadImgSizeVw) || 4.7;

        const MOBILE_RATIO = 80 / 38;
        const slotSizeMobile = (geometry && geometry.slotSizeVwMobile) || slotSize * MOBILE_RATIO;
        const beadSizeMobile = (geometry && geometry.beadImgSizeVwMobile) || beadSize * MOBILE_RATIO;

        this.el.slots.innerHTML = '';

        for (let index = 0; index < total; index += 1) {
          const beadId = this.state.slots[index];
          const bead = beadId ? this.findBead(beadId) : null;

          const slot = document.createElement('button');
          slot.type = 'button';
          slot.className = `bb-slot${bead ? ' bb-slot--filled' : ''}`;
          slot.dataset.slotIndex = String(index);
          slot.style.setProperty('--bb-slot-size', `${slotSize}vw`);
          slot.style.setProperty('--bb-bead-size', `${beadSize}vw`);
          slot.style.setProperty('--bb-slot-size-m', `${slotSizeMobile}vw`);
          slot.style.setProperty('--bb-bead-size-m', `${beadSizeMobile}vw`);
          if (!bead && this.ghostUrl) {
            slot.style.setProperty('--bb-ghost', `url("${this.ghostUrl}")`);
          }

          const spec = table ? table[index] : null;
          if (spec) {
            // "center" variants author the slot centre; older ones author its corner
            if (geometry && geometry.anchor === 'center') slot.classList.add('bb-slot--centred');
            for (const edge of ['top', 'right', 'bottom', 'left']) {
              if (spec[edge] != null) slot.style[edge] = spec[edge];
            }
            if (spec.rotate != null) slot.style.setProperty('--bb-slot-angle', `${spec.rotate}deg`);

            const nudge = beadId && spec.nudges ? spec.nudges[beadId] : null;
            if (nudge) {
              slot.style.setProperty('--bb-slot-nudge-x', `${nudge[0]}vw`);
              slot.style.setProperty('--bb-slot-nudge-y', `${nudge[1]}vw`);
            }
            const spin = beadId && spec.nudgeRotate ? spec.nudgeRotate[beadId] : null;
            if (spin != null) slot.style.setProperty('--bb-slot-angle', `${spin}deg`);
          } else {
            const { x, y, angle } = this.slotGeometry(index, total);
            slot.style.left = `${x}%`;
            slot.style.top = `${y}%`;
            slot.style.marginLeft = `${-slotSize / 2}vw`;
            slot.style.marginTop = `${-slotSize / 2}vw`;
            slot.style.setProperty('--bb-slot-angle', `${angle}deg`);
          }

          if (bead && bead.kind === 'detail') {
            const angle = parseFloat(slot.style.getPropertyValue('--bb-slot-angle')) || 0;
            const nx = parseFloat(slot.style.getPropertyValue('--bb-slot-nudge-x')) || 0;
            const ny = parseFloat(slot.style.getPropertyValue('--bb-slot-nudge-y')) || 0;
            if (nx || ny) {
              const rad = (angle * Math.PI) / 180;
              const cos = Math.cos(rad);
              const sin = Math.sin(rad);
              slot.style.setProperty('--bb-slot-nudge-x', `${nx * cos - ny * sin}vw`);
              slot.style.setProperty('--bb-slot-nudge-y', `${nx * sin + ny * cos}vw`);
            }
            slot.style.setProperty('--bb-slot-angle', '0deg');
          }

          const label = this.interpolate(this.strings.slot_label, '__N__', index + 1);
          slot.setAttribute(
            'aria-label',
            `${label}: ${bead ? this.t(bead.name) : this.strings.slot_empty || ''}`
          );

          if (bead) {
            slot.appendChild(this.beadVisual(bead));

            if (index === this.landingSlot) {
              slot.classList.add('bb-slot--landing');
              requestAnimationFrame(() =>
                requestAnimationFrame(() => slot.classList.remove('bb-slot--landing'))
              );
            }
          }

          this.bindSlot(slot, index);
          this.el.slots.appendChild(slot);
        }
      }

      renderCord() {
        const size = this.currentSize();
        const style = this.currentStyle();
        if (!size || !style || !this.el.cord) return;

        const cord = this.findCord(this.state.cordColorId);
        const placeholder = style.placeholder || {};
        const color = cord ? cord.hex : placeholder.cord || '#333';
        const width = placeholder.width || 2.5;

        const points = this.slotCentresPercent();

        let path;
        if (points.length >= 2) {
          const closed = Math.abs(size.slots.sweep) >= 359.5;
          path = this.smoothPath(points, closed);
        } else {
          const { cx, cy, rx, ry } = size.slots;
          path = `M ${cx - rx} ${cy} a ${rx} ${ry} 0 1 0 ${rx * 2} 0 a ${rx} ${ry} 0 1 0 ${-rx * 2} 0`;
        }

        this.el.cord.innerHTML =
          `<path d="${path}" fill="none" stroke="${this.escapeAttr(color)}" ` +
          `stroke-width="${width}" stroke-linecap="round" stroke-linejoin="round" />`;
      }

      slotCentresPercent() {
        const box = this.el.preview.getBoundingClientRect();
        if (!box.width || !box.height) return [];

        return [...this.el.slots.querySelectorAll('.bb-slot')].map((slot) => {
          const r = slot.getBoundingClientRect();
          return [
            ((r.left + r.width / 2 - box.left) / box.width) * 100,
            ((r.top + r.height / 2 - box.top) / box.height) * 100,
          ];
        });
      }

      smoothPath(points, closed) {
        const n = points.length;
        const at = (i) => points[closed ? (i + n) % n : Math.max(0, Math.min(n - 1, i))];
        const round = (v) => Math.round(v * 100) / 100;

        let d = `M ${round(points[0][0])} ${round(points[0][1])}`;
        const last = closed ? n : n - 1;

        for (let i = 0; i < last; i += 1) {
          const p0 = at(i - 1);
          const p1 = at(i);
          const p2 = at(i + 1);
          const p3 = at(i + 2);
          const c1 = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6];
          const c2 = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6];
          d +=
            ` C ${round(c1[0])} ${round(c1[1])}, ${round(c2[0])} ${round(c2[1])},` +
            ` ${round(p2[0])} ${round(p2[1])}`;
        }
        if (closed) d += ' Z';
        return d;
      }

      beadVisual(bead) {
        const wrapper = document.createElement('span');
        wrapper.className = 'bb-bead__visual';

        if (bead.image) {
          const img = document.createElement('img');
          img.src = this.assetUrl(bead.image);
          img.alt = '';
          img.loading = 'lazy';

          img.draggable = false;
          this.withImageFallback(img, wrapper, bead.hex);
          wrapper.appendChild(img);
        } else {
          wrapper.classList.add('bb-bead__visual--placeholder');
          wrapper.style.setProperty('--bb-bead-color', bead.hex || '#999');
        }
        return wrapper;
      }

      withImageFallback(img, wrapper, hex) {
        img.addEventListener(
          'error',
          () => {
            img.remove();
            wrapper.classList.add('bb-bead__visual--placeholder');
            wrapper.style.setProperty('--bb-bead-color', hex || '#999');
          },
          { once: true }
        );
      }

      assetUrl(filename) {
        if (!filename) return '';
        if (filename.startsWith('http') || filename.startsWith('/')) return filename;

        const artworkBase = this.config && this.config.artwork && this.config.artwork.basePath;
        return (artworkBase || this.assetBase) + filename;
      }

      renderSpec() {
        const style = this.currentStyle();
        const size = this.currentSize();
        if (!style) return;

        this.el.styleName.textContent = this.t(style.name);

        const rows = [[this.strings.spec_style, this.t(style.name)]];
        if (size) rows.push([this.strings.spec_size, this.t(size.label)]);
        if (style.hasBeadSize) {
          const beadSize = (this.config.beadSizes || []).find(
            (candidate) => candidate.id === this.state.beadSize
          );
          if (beadSize) rows.push([this.strings.spec_bead_size, this.t(beadSize.label)]);
        }
        if (style.hasCordColor) {
          const cord = this.findCord(this.state.cordColorId);
          if (cord) rows.push([this.strings.spec_color, `${cord.id} — ${this.t(cord.name)}`]);
        }
        if (style.hasClasp) {
          const clasp = this.findClasp(this.state.claspId);
          if (clasp) rows.push([this.strings.spec_clasp, `${clasp.id} — ${this.t(clasp.name)}`]);
        }
        rows.push([this.strings.spec_base_price, this.formatMoney(this.basePrice())]);

        const previous = this.lastSpecValues || {};
        this.el.spec.innerHTML = rows
          .map(
            ([label, value]) =>
              `<div class="bb-spec__row"><dt>${this.escape(label)}</dt>` +
              `<dd data-spec-key="${this.escapeAttr(label)}">${this.escape(value)}</dd></div>`
          )
          .join('');

        if (this.lastSpecValues) {
          for (const [label, value] of rows) {
            if (previous[label] === value) continue;
            const node = this.el.spec.querySelector(`[data-spec-key="${CSS.escape(label)}"]`);
            if (node) this.flash(node);
          }
        }
        this.lastSpecValues = Object.fromEntries(rows);
      }

      flash(node) {
        node.classList.remove('bb-flash');

        void node.offsetWidth;
        node.classList.add('bb-flash');
        setTimeout(() => node.classList.remove('bb-flash'), 1000);
      }

      renderLedger() {
        const counts = new Map();
        for (const beadId of this.state.slots) {
          if (!beadId) continue;
          counts.set(beadId, (counts.get(beadId) || 0) + 1);
        }

        if (counts.size === 0) {
          this.el.ledger.innerHTML = '';
          return;
        }

        const rows = [...counts.entries()]
          .map(([beadId, count]) => {
            const bead = this.findBead(beadId);
            if (!bead) return '';
            const lineTotal = this.beadPrice(bead) * count;
            return (
              `<tr><td>${this.escape(this.t(bead.name))}</td>` +
              `<td>×${count}</td>` +
              `<td>${this.escape(this.formatMoney(lineTotal))}</td></tr>`
            );
          })
          .join('');

        this.el.ledger.innerHTML =
          `<table class="bb-ledger__table"><thead><tr>` +
          `<th>${this.escape(this.strings.table_bead)}</th>` +
          `<th>${this.escape(this.strings.table_qty)}</th>` +
          `<th>${this.escape(this.strings.table_price)}</th>` +
          `</tr></thead><tbody>${rows}</tbody></table>`;
      }

      basePrice() {
        const style = this.currentStyle();
        if (!style) return 0;
        let price = Number(style.basePrice) || 0;
        const clasp = this.findClasp(this.state.claspId);
        if (style.hasClasp && clasp) price += Number(clasp.price) || 0;
        return price;
      }

      exactTotal() {
        return this.state.slots.reduce((sum, beadId) => {
          const bead = beadId ? this.findBead(beadId) : null;
          return sum + this.beadPrice(bead);
        }, this.basePrice());
      }

      chargeableVariant() {
        const exact = this.exactTotal();
        const prices = [...this.priceIndex.keys()].sort((a, b) => a - b);

        if (prices.length === 0) return null;

        if (prices.length === 1) {
          return { price: exact, id: this.priceIndex.get(prices[0]), exact: true };
        }

        const stepped = Math.ceil(exact / this.priceStep) * this.priceStep;
        const match = prices.find((price) => price >= stepped - 1e-9);
        if (match == null) return null;

        if (match > stepped + this.priceStep - 1e-9) {
          console.warn(
            `bracelet-builder: no price variant near ${stepped} (nearest above is ${match}). ` +
              'The carrier product is missing price steps — regenerate its variant CSV.'
          );
          return null;
        }

        return { price: match, id: this.priceIndex.get(match) };
      }

      renderPrice() {
        const variant = this.chargeableVariant();
        const display = variant ? variant.price : this.exactTotal();
        const text = this.formatMoney(display);

        if (this.lastPriceText !== undefined && this.lastPriceText !== text) {
          this.flash(this.el.price);
        }
        this.lastPriceText = text;
        this.el.price.textContent = text;
      }

      renderProgress() {
        const remaining = this.state.slots.filter((beadId) => !beadId).length;
        const variant = this.chargeableVariant();
        const complete = remaining === 0;

        if (!complete) {
          this.el.progress.textContent = this.interpolate(
            this.strings.remaining,
            '__N__',
            remaining
          );
        } else if (!variant) {
          this.el.progress.textContent = this.strings.unavailable_price || '';
        } else {
          this.el.progress.textContent = this.strings.complete || '';
        }

        this.el.add.disabled = !complete || !variant;
        this.classList.toggle('bb--complete', complete);
        this.showError(null);
      }

      sizeLabel(size) {
        const name = this.t(size.label);
        const cm = Number(size.lengthCm);
        if (!cm) return name;
        return `${name} (${cm}${this.strings.unit_cm || 'cm'})`;
      }

      audiences() {
        return [
          { id: 'male', name: this.strings.audience_male || 'Men' },
          { id: 'female', name: this.strings.audience_female || 'Women' },
        ];
      }

      beadsFor(group) {
        const audience = this.state.audience;
        const style = this.currentStyle();
        const mm = style && style.hasBeadSize ? this.state.beadSize : null;
        const collection = (this.collections || []).find((c) => c.id === audience);
        const order = new Map(
          (collection && Array.isArray(collection.beads) ? collection.beads : []).map((id, i) => [id, i])
        );
        const rank = (bead) => (order.has(bead.id) ? order.get(bead.id) : order.size);
        return (group.beads || [])
          .filter((bead) => {
            if (audience && bead.audience && bead.audience !== 'both' && bead.audience !== audience) {
              return false;
            }
            if (mm && bead.price && typeof bead.price === 'object') {
              const sizes = Object.keys(bead.price);
              if (sizes.length && !sizes.includes(mm)) return false;
            }
            return true;
          })
          .sort((a, b) => rank(a) - rank(b));
      }

      selectAudience(id) {
        if (!this.audiences().some((a) => a.id === id)) return;
        if (this.state.audience === id) return;

        this.state.audience = id;

        // women have XS, men do not; and the stone count changes with gender
        const sizes = this.sizesFor(this.currentStyle(), id);
        if (sizes.length && !sizes.some((z) => z.id === this.state.sizeId)) {
          this.state.sizeId = (sizes[Math.floor(sizes.length / 2)] || sizes[0]).id;
        }
        if (this.state.slots.length !== this.slotCount()) this.resetSlots();

        const available = new Set(
          (this.config.beadGroups || []).flatMap((g) => this.beadsFor(g)).map((b) => b.id)
        );
        this.state.slots = this.state.slots.map((beadId) =>
          beadId && !available.has(beadId) ? null : beadId
        );
        if (this.state.claspId && !this.claspsFor().some((c) => c.id === this.state.claspId)) {
          this.state.claspId = null;
        }

        this.renderNav();
        this.renderPanels();
        this.refresh();
      }

      navItems() {
        const style = this.currentStyle();
        if (!style) return [];

        const items = [
          { id: 'audience', label: this.strings.audience },
          { id: 'style', label: this.strings.style },
          { id: 'size', label: this.strings.size },
        ];
        if (style.hasBeadSize) items.push({ id: 'beadSize', label: this.strings.bead_size });
        items.push({ id: 'beads', label: this.strings.beads });
        if (style.hasCordColor) items.push({ id: 'cord', label: this.strings.cord_color });
        if (style.hasClasp) items.push({ id: 'clasp', label: this.strings.clasp });
        return items;
      }

      renderNav() {
        this.el.nav.innerHTML = '';
        for (const item of this.navItems()) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'bb-nav__item';
          button.dataset.panel = item.id;
          button.textContent = item.label || item.id;
          button.setAttribute('aria-expanded', 'false');
          button.addEventListener('click', () => this.togglePanel(item.id));
          this.el.nav.appendChild(button);
        }
      }

      syncNavVisibility() {
        const wanted = new Set(this.navItems().map((item) => item.id));
        const rendered = new Set(
          [...this.el.nav.querySelectorAll('[data-panel]')].map((node) => node.dataset.panel)
        );
        const same =
          wanted.size === rendered.size && [...wanted].every((id) => rendered.has(id));
        if (!same) {
          this.renderNav();
          this.renderPanels();
        }
      }

      togglePanel(panelId) {
        if (this.state.openPanel === panelId) return this.closePanel();
        this.openPanel(panelId);
      }

      openPanel(panelId) {
        this.state.openPanel = panelId;
        for (const panel of this.el.panels.querySelectorAll('[data-panel-body]')) {
          panel.classList.toggle('bb-panel--open', panel.dataset.panelBody === panelId);
        }
        for (const button of this.el.nav.querySelectorAll('[data-panel]')) {
          button.setAttribute('aria-expanded', String(button.dataset.panel === panelId));
          button.classList.toggle('bb-nav__item--active', button.dataset.panel === panelId);
        }
        this.classList.add('bb--panel-open');
      }

      closePanel() {
        this.state.openPanel = null;
        for (const panel of this.el.panels.querySelectorAll('[data-panel-body]')) {
          panel.classList.remove('bb-panel--open');
        }
        for (const button of this.el.nav.querySelectorAll('[data-panel]')) {
          button.setAttribute('aria-expanded', 'false');
          button.classList.remove('bb-nav__item--active');
        }
        this.classList.remove('bb--panel-open');
      }

      renderPanels() {
        // Picking an option rebuilds every panel, which would otherwise throw the
        // shopper back to the top of a long list. Carry the scroll offsets over.
        const offsets = new Map();
        for (const panel of this.el.panels.querySelectorAll('[data-panel-body]')) {
          const scroller = panel.querySelector('.bb-panel__scroll');
          if (scroller && scroller.scrollTop) offsets.set(panel.dataset.panelBody, scroller.scrollTop);
        }

        this.el.panels.innerHTML = '';
        for (const item of this.navItems()) {
          this.el.panels.appendChild(this.buildPanel(item));
        }

        for (const [id, top] of offsets) {
          const scroller = this.el.panels.querySelector(
            `[data-panel-body="${id}"] .bb-panel__scroll`
          );
          if (scroller) scroller.scrollTop = top;
        }

        // A rebuilt panel is a brand new node, so re-opening it would replay the
        // slide-in and read as a fresh drawer. Suppress it for this frame only.
        if (this.state.openPanel) {
          const panel = this.el.panels.querySelector(
            `[data-panel-body="${this.state.openPanel}"]`
          );
          if (panel) {
            panel.classList.add('bb-panel--instant');
            requestAnimationFrame(() =>
              requestAnimationFrame(() => panel.classList.remove('bb-panel--instant'))
            );
          }
        }
      }

      buildPanel(item) {
        const panel = document.createElement('div');
        panel.className = 'bb-panel';
        panel.dataset.panelBody = item.id;
        panel.setAttribute('role', 'region');
        panel.setAttribute('aria-label', item.label || item.id);

        const scroller = document.createElement('div');
        scroller.className = 'bb-panel__scroll';
        scroller.appendChild(this.buildPanelContent(item.id));
        panel.appendChild(scroller);

        const footer = document.createElement('div');
        footer.className = 'bb-panel__footer';

        const close = document.createElement('button');
        close.type = 'button';
        close.className = 'bb-panel__action bb-panel__action--close';
        close.setAttribute('aria-label', this.strings.close || 'Close');
        close.innerHTML =
          '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">' +
          '<path d="M4 4 L16 16 M16 4 L4 16" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linecap="round"/></svg>';
        close.addEventListener('click', () => this.closePanel());

        const confirm = document.createElement('button');
        confirm.type = 'button';
        confirm.className = 'bb-panel__action bb-panel__action--confirm';
        confirm.setAttribute('aria-label', this.strings.confirm || this.strings.close || 'Done');
        confirm.innerHTML =
          '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">' +
          '<path d="M4 10.5 L8 14.5 L16 5.5" stroke="currentColor" stroke-width="1.8" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        confirm.addEventListener('click', () => this.closePanel());

        footer.appendChild(close);
        footer.appendChild(confirm);
        panel.appendChild(footer);

        return panel;
      }

      buildPanelContent(panelId) {
        switch (panelId) {
          case 'audience':
            return this.buildCardGrid(
              this.audiences(),
              (a) => a.id === this.state.audience,
              (a) => this.selectAudience(a.id),
              (a) => ({ label: a.name, image: '', hex: '#e1e1e1' })
            );

          case 'style':
            return this.buildCardGrid(
              this.config.styles || [],
              (style) => style.id === this.state.styleId,
              (style) => this.selectStyle(style.id),
              (style) => ({
                label: this.t(style.name),
                image: style.image,

                hex: (style.placeholder && style.placeholder.cord) || '#333',
              })
            );

          case 'size': {
            const style = this.currentStyle();
            return this.buildRowList(
              this.sizesFor(style),
              (size) => size.id === this.state.sizeId,
              (size) => this.selectSize(size.id),
              (size) => this.sizeLabel(size)
            );
          }

          case 'beadSize':
            return this.buildRowList(
              this.beadSizesFor(),
              (size) => size.id === this.state.beadSize,
              (size) => this.selectBeadSize(size.id),
              (size) => this.t(size.label)
            );

          case 'cord':
            return this.buildCardGrid(
              this.config.cordColors || [],
              (cord) => cord.id === this.state.cordColorId,
              (cord) => this.selectCord(cord.id),
              (cord) => ({ label: this.t(cord.name), image: null, hex: cord.hex }),
              { restructures: false }
            );

          case 'clasp':
            return this.buildCardGrid(
              this.claspsFor(),
              (clasp) => clasp.id === this.state.claspId,
              (clasp) => this.selectClasp(clasp.id),
              (clasp) => ({
                label: this.t(clasp.name),
                image: clasp.overlay || this.lockAssetFor(clasp),
                crop: this.claspCrop(),
                hex: clasp.hex,
                note: clasp.price ? `+${this.formatMoney(clasp.price)}` : '',
              }),
              { restructures: false }
            );

          case 'beads':
            return this.buildBeadPalette();

          default:
            return document.createDocumentFragment();
        }
      }

      buildCardGrid(items, isActive, onSelect, describe, options) {
        const grid = document.createElement('div');
        grid.className = 'bb-grid';
        // Some picks change which panels exist (style, audience); those need a
        // rebuild. Colour and clasp only change the preview, so rebuilding there
        // would tear the drawer down and replay its slide-in for nothing.
        const restructures = !options || options.restructures !== false;
        const cards = [];

        for (const item of items) {
          const info = describe(item);
          const card = document.createElement('button');
          card.type = 'button';
          card.className = `bb-card${isActive(item) ? ' bb-card--active' : ''}`;
          card.setAttribute('aria-pressed', String(isActive(item)));

          const media = document.createElement('span');
          media.className = 'bb-card__media';
          if (info.image && info.crop) {
            const { cx, cy, size, w } = info.crop;
            media.classList.add('bb-card__media--crop');
            media.style.setProperty('--bb-crop-w', `${(100 * w) / size}%`);
            media.style.setProperty('--bb-crop-x', `${(-100 * (cx - size / 2)) / size}%`);
            media.style.setProperty('--bb-crop-y', `${(-100 * (cy - size / 2)) / size}%`);
            const img = document.createElement('img');
            img.src = this.assetUrl(info.image);
            img.alt = '';
            img.loading = 'lazy';
            img.addEventListener(
              'error',
              () => {
                img.remove();
                media.classList.remove('bb-card__media--crop');
                media.classList.add('bb-card__media--swatch');
                if (info.hex) media.style.background = info.hex;
              },
              { once: true }
            );
            media.appendChild(img);
          } else if (info.image) {
            const img = document.createElement('img');
            img.src = this.assetUrl(info.image);
            img.alt = '';
            img.loading = 'lazy';
            img.addEventListener(
              'error',
              () => {
                img.remove();
                media.classList.add('bb-card__media--swatch');
                if (info.hex) media.style.background = info.hex;
              },
              { once: true }
            );
            media.appendChild(img);
          } else if (info.hex) {
            media.classList.add('bb-card__media--swatch');
            media.style.background = info.hex;
          }
          card.appendChild(media);

          const label = document.createElement('span');
          label.className = 'bb-card__label';
          label.textContent = info.label;
          card.appendChild(label);

          if (info.note) {
            const note = document.createElement('span');
            note.className = 'bb-card__note';
            note.textContent = info.note;
            card.appendChild(note);
          }

          card.addEventListener('click', () => {
            onSelect(item);
            if (restructures) {
              this.renderPanels();
              this.openPanel(this.state.openPanel);
              return;
            }
            for (const [node, value] of cards) {
              const on = isActive(value);
              node.classList.toggle('bb-card--active', on);
              node.setAttribute('aria-pressed', String(on));
            }
          });
          cards.push([card, item]);
          grid.appendChild(card);
        }
        return grid;
      }

      buildRowList(items, isActive, onSelect, label) {
        const list = document.createElement('div');
        list.className = 'bb-rows';

        for (const item of items) {
          const row = document.createElement('button');
          row.type = 'button';
          row.className = `bb-row${isActive(item) ? ' bb-row--active' : ''}`;
          row.setAttribute('aria-pressed', String(isActive(item)));
          row.textContent = label(item);
          row.addEventListener('click', () => {
            onSelect(item);
            this.renderPanels();
            this.openPanel(this.state.openPanel);
          });
          list.appendChild(row);
        }
        return list;
      }

      buildBeadPalette() {
        const fragment = document.createDocumentFragment();

        const hint = document.createElement('p');
        hint.className = 'bb-hint';
        hint.textContent = this.strings.drag_hint || '';
        fragment.appendChild(hint);

        for (const group of this.config.beadGroups || []) {
          const heading = document.createElement('h3');
          heading.className = 'bb-group';
          heading.textContent = this.t(group.name);
          fragment.appendChild(heading);

          const grid = document.createElement('div');
          grid.className = 'bb-grid bb-grid--beads';

          for (const bead of this.beadsFor(group)) {
            grid.appendChild(this.buildBeadCard(bead));
          }
          fragment.appendChild(grid);
        }
        return fragment;
      }

      buildBeadCard(bead) {
        const card = document.createElement('button');
        card.type = 'button';
        card.className = 'bb-bead';
        card.dataset.beadId = bead.id;
        card.setAttribute(
          'aria-label',
          `${this.t(bead.name)} — ${this.formatMoney(this.beadPrice(bead))}. ${this.strings.place_bead || ''}`
        );

        const visual = this.beadVisual(bead);
        visual.classList.add('bb-bead__swatch');
        card.appendChild(visual);

        const name = document.createElement('span');
        name.className = 'bb-bead__name';
        name.textContent = this.t(bead.name);
        card.appendChild(name);

        const keywords = document.createElement('span');
        keywords.className = 'bb-bead__keywords';
        keywords.textContent = this.t(bead.keywords);
        card.appendChild(keywords);

        const price = document.createElement('span');
        price.className = 'bb-bead__price';
        price.textContent = this.formatMoney(this.beadPrice(bead));
        card.appendChild(price);

        this.bindBeadSource(card, bead.id);
        return card;
      }

      bindBeadSource(card, beadId) {
        let pointerId = null;
        let startX = 0;
        let startY = 0;
        let grabX = 0;
        let grabY = 0;
        let dragging = false;
        let ghost = null;
        let ghostW = 0;
        let ghostH = 0;
        let targets = null;
        let hovered = null;
        let frame = 0;
        let pendingX = 0;
        let pendingY = 0;

        const measure = () => {
          const list = [...this.el.slots.querySelectorAll('.bb-slot')];
          targets = list.map((slot) => {
            const r = slot.getBoundingClientRect();
            return {
              slot,
              cx: r.left + r.width / 2,
              cy: r.top + r.height / 2,

              radius: Math.max(r.width, r.height) * 0.75,
            };
          });
        };

        const nearest = (x, y) => {
          if (!targets) return null;
          let best = null;
          let bestDist = Infinity;
          for (const t of targets) {
            const d = Math.hypot(x - t.cx, y - t.cy);
            if (d < t.radius && d < bestDist) {
              bestDist = d;
              best = t;
            }
          }
          return best;
        };

        const setHover = (target) => {
          const next = target ? target.slot : null;
          if (next === hovered) return;
          if (hovered) hovered.classList.remove('bb-slot--hover');
          if (next) next.classList.add('bb-slot--hover');
          hovered = next;
        };

        const hitPoint = () => [pendingX + ghostW / 2, pendingY + ghostH / 2];

        const paint = () => {
          frame = 0;
          if (!ghost) return;
          ghost.style.transform = `translate3d(${pendingX}px, ${pendingY}px, 0)`;
          setHover(nearest(...hitPoint()));
        };

        const schedule = (x, y) => {
          pendingX = x;
          pendingY = y;
          if (!frame) frame = requestAnimationFrame(paint);
        };

        const cleanup = () => {
          if (frame) cancelAnimationFrame(frame);
          frame = 0;
          if (ghost) ghost.remove();
          ghost = null;
          setHover(null);
          targets = null;
          dragging = false;
          pointerId = null;
          this.classList.remove('bb--dragging');
          document.removeEventListener('pointermove', onMove);
          document.removeEventListener('pointerup', onUp);
          document.removeEventListener('pointercancel', cleanup);
        };

        const revert = () => {
          if (!ghost) return cleanup();
          const r = card.querySelector('.bb-bead__visual').getBoundingClientRect();
          ghost.style.transition = 'transform 0.25s ease-out, opacity 0.25s ease-out';
          ghost.style.transform = `translate3d(${r.left}px, ${r.top}px, 0)`;
          ghost.style.opacity = '0';
          const node = ghost;
          ghost = null;
          setTimeout(() => node.remove(), 260);
          cleanup();
        };

        const onMove = (event) => {
          if (event.pointerId !== pointerId) return;

          if (!dragging) {
            if (Math.hypot(event.clientX - startX, event.clientY - startY) <= 6) return;
            dragging = true;
            this.classList.add('bb--dragging');

            const bead = this.findBead(beadId);
            const source = card.querySelector('.bb-bead__visual');
            const r = source.getBoundingClientRect();
            grabX = startX - r.left;
            grabY = startY - r.top;

            ghostW = r.width;
            ghostH = r.height;
            ghost = document.createElement('div');
            ghost.className = 'bb-ghost';
            ghost.style.width = `${ghostW}px`;
            ghost.style.height = `${ghostH}px`;
            if (bead) ghost.appendChild(this.beadVisual(bead));
            document.body.appendChild(ghost);

            measure();
          }

          event.preventDefault();

          schedule(event.clientX - grabX, event.clientY - grabY);
        };

        const onUp = (event) => {
          if (event.pointerId !== pointerId) return;

          if (!dragging) {
            cleanup();

            this.armBead(beadId);
            return;
          }

          const target = nearest(...hitPoint());
          if (target) {
            this.placeBead(Number(target.slot.dataset.slotIndex), beadId);
            cleanup();
          } else {
            revert();
          }
        };

        let touchTap = false;

        card.addEventListener('pointerdown', (event) => {
          if (event.button != null && event.button !== 0) return;

          if (event.pointerType === 'touch') {
            touchTap = true;
            return;
          }

          pointerId = event.pointerId;
          startX = event.clientX;
          startY = event.clientY;

          if (card.setPointerCapture) {
            try {
              card.setPointerCapture(event.pointerId);
            } catch (error) {
            }
          }
          document.addEventListener('pointermove', onMove, { passive: false });
          document.addEventListener('pointerup', onUp);
          document.addEventListener('pointercancel', cleanup);
        });

        card.addEventListener('click', () => {
          if (!touchTap) return;
          touchTap = false;
          this.armBead(beadId);
        });

        card.addEventListener('dragstart', (event) => event.preventDefault());

        card.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          this.armBead(beadId);
        });
      }

      bindSlot(slot, index) {
        slot.addEventListener('click', () => {
          if (this.state.armedBeadId) {
            this.placeBead(index, this.state.armedBeadId);

            return;
          }
          if (this.state.slots[index]) this.removeBead(index);
        });

        slot.addEventListener('keydown', (event) => {
          if (event.key === 'Delete' || event.key === 'Backspace') {
            event.preventDefault();
            this.removeBead(index);
          }
        });
      }

      armBead(beadId) {
        this.state.armedBeadId = beadId;
        this.classList.add('bb--armed');
        for (const card of this.el.panels.querySelectorAll('[data-bead-id]')) {
          card.classList.toggle('bb-bead--armed', card.dataset.beadId === beadId);
        }
        const firstEmpty = this.el.slots.querySelector('.bb-slot:not(.bb-slot--filled)');
        if (firstEmpty) firstEmpty.focus({ preventScroll: true });
      }

      disarmBead() {
        this.state.armedBeadId = null;
        this.classList.remove('bb--armed');
        for (const card of this.el.panels.querySelectorAll('[data-bead-id]')) {
          card.classList.remove('bb-bead--armed');
        }
      }

      slotAt(clientX, clientY) {
        for (const slot of this.el.slots.querySelectorAll('.bb-slot')) {
          const rect = slot.getBoundingClientRect();
          if (
            clientX >= rect.left &&
            clientX <= rect.right &&
            clientY >= rect.top &&
            clientY <= rect.bottom
          ) {
            return slot;
          }
        }
        return null;
      }

      highlightSlotAt(clientX, clientY) {
        const target = this.slotAt(clientX, clientY);
        for (const slot of this.el.slots.querySelectorAll('.bb-slot')) {
          slot.classList.toggle('bb-slot--hover', slot === target);
        }
      }

      clearSlotHighlight() {
        for (const slot of this.el.slots.querySelectorAll('.bb-slot')) {
          slot.classList.remove('bb-slot--hover');
        }
      }

      capturePreview(maxChars = 16000) {
        const box = this.el.preview;
        if (!box || !this.usingArtwork()) return '';

        const rect = box.getBoundingClientRect();
        if (!rect.width || !rect.height) return '';

        const width = 320;
        const scale = width / rect.width;
        const height = Math.round(rect.height * scale);

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) return '';

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);

        const place = (node) => {
          if (!node || !node.complete || !node.naturalWidth) return;
          const r = node.getBoundingClientRect();
          if (!r.width || !r.height) return;

          const w = (node.offsetWidth || r.width) * scale;
          const h = (node.offsetHeight || r.height) * scale;
          const cx = (r.left + r.width / 2 - rect.left) * scale;
          const cy = (r.top + r.height / 2 - rect.top) * scale;

          try {
            ctx.drawImage(node, cx - w / 2, cy - h / 2, w, h);
          } catch (error) {
            return;
          }
        };

        place(this.el.base);
        if (this.el.slots) {
          for (const img of this.el.slots.querySelectorAll('img')) place(img);
        }
        place(this.el.front);
        place(this.el.lock);

        try {
          let quality = 0.72;
          let data = canvas.toDataURL('image/jpeg', quality);
          while (data.length > maxChars && quality > 0.35) {
            quality -= 0.08;
            data = canvas.toDataURL('image/jpeg', quality);
          }
          return data.length <= maxChars ? data : '';
        } catch (error) {
          console.warn('bracelet-builder: preview capture failed', error);
          return '';
        }
      }

      configPayload() {
        const style = this.currentStyle();
        const size = this.currentSize();
        return {
          v: 1,
          audience: this.state.audience,
          style: style ? style.id : null,
          size: size ? size.id : null,
          beadSize: this.state.beadSize,
          cord: this.state.cordColorId,
          clasp: this.state.claspId,
          slots: this.state.slots.slice(),
          exactTotal: Math.round(this.exactTotal() * 100) / 100,
        };
      }

      currentQuantity() {
        const raw = this.el.qty ? parseInt(this.el.qty.value, 10) : 1;
        return Number.isFinite(raw) && raw > 0 ? Math.min(raw, 99) : 1;
      }

      beadSummary() {
        const counts = new Map();
        for (const beadId of this.state.slots) {
          if (!beadId) continue;
          counts.set(beadId, (counts.get(beadId) || 0) + 1);
        }
        return [...counts.entries()]
          .map(([beadId, count]) => {
            const bead = this.findBead(beadId);
            return `${bead ? this.t(bead.name) : beadId} ×${count}`;
          })
          .join(', ');
      }

      async addToCart() {
        const variant = this.chargeableVariant();
        if (!variant || !variant.id) return this.showError(this.strings.unavailable_price);
        if (this.state.slots.some((beadId) => !beadId)) return;

        this.el.add.disabled = true;
        this.el.add.textContent = this.strings.adding || '';
        this.showError(null);

        const style = this.currentStyle();
        const size = this.currentSize();
        const properties = {
          _config: JSON.stringify(this.configPayload()),
        };

        const preview = this.capturePreview();
        if (preview) properties._preview = preview;
        properties[this.strings.spec_style] = style ? this.t(style.name) : '';
        if (size) properties[this.strings.spec_size] = this.t(size.label);
        if (style && style.hasBeadSize && this.state.beadSize) {
          properties[this.strings.spec_bead_size] = `${this.state.beadSize}mm`;
        }
        if (style && style.hasCordColor) {
          const cord = this.findCord(this.state.cordColorId);
          if (cord) properties[this.strings.spec_color] = this.t(cord.name);
        }
        if (style && style.hasClasp) {
          const clasp = this.findClasp(this.state.claspId);
          if (clasp) properties[this.strings.spec_clasp] = this.t(clasp.name);
        }
        properties[this.strings.table_bead] = this.beadSummary();

        try {
          const response = await fetch(this.dataset.cartAddUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items: [{ id: variant.id, quantity: this.currentQuantity(), properties }],
            }),
          });

          if (!response.ok) {
            const detail = await response.json().catch(() => ({}));
            throw new Error(detail.description || detail.message || `HTTP ${response.status}`);
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bracelet-builder' });
          }

          window.location.href = this.dataset.cartUrl;
        } catch (error) {
          console.error('bracelet-builder: add to cart failed', error);
          this.showError(error.message || this.strings.error);
          this.el.add.disabled = false;
          this.el.add.textContent = this.strings.add_to_cart || '';
        }
      }

      formatMoney(amount) {
        const value = Number(amount) || 0;
        try {
          return new Intl.NumberFormat(this.locale, {
            style: 'currency',
            currency: (this.config && this.config.settings && this.config.settings.currency) || 'GEL',
            maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
          }).format(value);
        } catch (error) {
          return String(value);
        }
      }

      interpolate(template, token, value) {
        return String(template || '').replace(token, value);
      }

      escape(value) {
        const div = document.createElement('div');
        div.textContent = String(value == null ? '' : value);
        return div.innerHTML;
      }

      escapeAttr(value) {
        return String(value == null ? '' : value).replace(/"/g, '&quot;');
      }

      debounce(fn, wait) {
        let timer;
        return (...args) => {
          clearTimeout(timer);
          timer = setTimeout(() => fn.apply(this, args), wait);
        };
      }
    }
  );
}
